/*
 * Copyright '2025' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { BaseBannerAlertsProvider } from "sirius-platform-support-library/shared/banner-alerts/providers/base-banner-alerts.provider";
import { ContextualRequestedBannerAlert } from "sirius-platform-support-library/shared/banner-alerts/banner-alert";
import { ServicePageNotificationService } from "../services/service-page-notification.service";
import { BannerAlertsMapper } from "../mappers/banner-alerts.mapper";
import {
  AlertType,
  ServicePageNotification,
  ServicePageNotificationsType,
} from "../../models/service-page-notification.model";
import { IAuthenticationService } from "sirius-platform-support-library/shared/authentication/authentication-service.interface";
import { ILocalizationService } from "sirius-platform-support-library/shared/localization/localization-service.interface";
import { TogglesProvider } from "../../toggles/toggles-provider";
import {
  ENABLE_BACKUP_ALERT,
  LocaleBackupAlerts,
} from "../mappers/banners.constants";
import { PermissionsProviderRefreshRequestedCallback } from "sirius-platform-support-library/shared/authorization/providers/permissions-provider.interface";
import { BannerAlertsProviderRefreshRequestedCallback } from "sirius-platform-support-library/shared/banner-alerts/providers/banner-alerts-provider.interface";

export const BannerAlertsProviderTypeName = "BannerAlertsProvider";

export class BannerAlertsProvider extends BaseBannerAlertsProvider {
  public static readonly NAMESPACE = "tdp";

  private bannerAlerts: ContextualRequestedBannerAlert[] = [];
  public refreshRequestedCallback?: BannerAlertsProviderRefreshRequestedCallback;

  private readonly bannerMapper: BannerAlertsMapper;
  private readonly authenticationService: IAuthenticationService;
  private readonly servicePageNotificationService: ServicePageNotificationService;
  private readonly localizationService: ILocalizationService;
  private readonly togglesProvider: TogglesProvider;

  public constructor(
    bannerMapper: BannerAlertsMapper,
    servicePageNotificationService: ServicePageNotificationService,
    authenticationService: IAuthenticationService,
    localizationService: ILocalizationService,
    togglesProvider: TogglesProvider
  ) {
    super(BannerAlertsProvider.NAMESPACE);

    this.bannerMapper = bannerMapper;
    this.servicePageNotificationService = servicePageNotificationService;
    this.authenticationService = authenticationService;
    this.localizationService = localizationService;
    this.togglesProvider = togglesProvider;
  }

  public getNamespace(): string {
    return BannerAlertsProvider.NAMESPACE;
  }

  public async getInternalBanners(): Promise<void> {
    try {
      const serviceBannerAlerts =
        await this.servicePageNotificationService.getBannerAlerts();
      const filteredBannerAlerts = await this.filterBanners(
        serviceBannerAlerts
      );
      const currentLocaleCode = this.localizationService.getCurrentLocaleCode();
      const supportedLocale = this.localizationService
        .getSupportedLocales()
        .map((locale) => locale.localeCode);
      this.bannerAlerts =
        this.bannerMapper.mapToContextualRequestedBannersAlert(
          this,
          filteredBannerAlerts,
          currentLocaleCode,
          supportedLocale
        );
      await this.getBackupAlert(currentLocaleCode, supportedLocale);
    } catch (e) {
      console.error(e);
    }
  }
  public onRefreshRequested(
    callback: PermissionsProviderRefreshRequestedCallback
  ): void {
    this.refreshRequestedCallback = callback;
  }
  public async refresh(): Promise<void> {
    try {
      await this.getBannerAlerts();
      if (this.refreshRequestedCallback) {
        this.refreshRequestedCallback(this.getNamespace());
      }
    } catch (e) {
      console.error(e);
    }
  }

  private async getBackupAlert(
    currentLocaleCode: string,
    supportedLocale: string[]
  ): Promise<void> {
    const isBackupAlertEnabled = await this.togglesProvider.isActive(
      ENABLE_BACKUP_ALERT
    );
    if (isBackupAlertEnabled) {
      const backupAlert = this.buildBackupAlert();
      const mappedBackupAlert =
        this.bannerMapper.mapToContextualRequestedBannerAlert(
          this,
          backupAlert,
          currentLocaleCode,
          supportedLocale
        );
      this.bannerAlerts = mappedBackupAlert
        ? this.bannerAlerts.concat(mappedBackupAlert)
        : this.bannerAlerts;
    }
  }

  private buildBackupAlert(): ServicePageNotification {
    return {
      id: "backupAlert",
      location: AlertType.GAU,
      severity: ServicePageNotificationsType.WARNING,
      localeToMessage: LocaleBackupAlerts,
      messageData: {},
      startDate: 0,
      endDate: Number.MAX_SAFE_INTEGER,
      action: null,
      dismissible: false,
    };
  }

  private async filterBanners(
    servicePageNotifications: ServicePageNotification[]
  ): Promise<ServicePageNotification[]> {
    const filteredBasedOnDateBanners = await this.filterBasedOnDate(
      servicePageNotifications
    );
    return this.filterBasedOnLocation(filteredBasedOnDateBanners);
  }

  private async filterBasedOnDate(
    servicePageNotifications: ServicePageNotification[]
  ): Promise<ServicePageNotification[]> {
    const timeZoneOffset = new Date().getTimezoneOffset() * 60000;
    const currentDate = Date.now() + timeZoneOffset;

    const filteredServicePageNotifications = servicePageNotifications.filter(
      (servicePage) => {
        if (servicePage?.startDate && !servicePage?.endDate) {
          return currentDate >= new Date(servicePage.startDate).getTime();
        }
        if (servicePage?.startDate && servicePage?.endDate) {
          return (
            currentDate >= new Date(servicePage.startDate).getTime() &&
            currentDate <= new Date(servicePage.endDate).getTime()
          );
        }
        return !servicePage?.startDate && !servicePage?.endDate;
      }
    );
    return filteredServicePageNotifications;
  }

  private async filterBasedOnLocation(
    servicePageNotifications: ServicePageNotification[]
  ): Promise<ServicePageNotification[]> {
    const bannerAlerts = [];
    const gauNotifications = servicePageNotifications.filter(
      (bannerAlerts) => bannerAlerts.location === "GAU"
    );
    bannerAlerts.push(...gauNotifications);
    if (this.authenticationService.isUserAuthenticated()) {
      const gsiNotifications = servicePageNotifications.filter(
        (bannerAlerts) => bannerAlerts.location === "GSI"
      );
      bannerAlerts.push(...gsiNotifications);
    }
    return bannerAlerts;
  }

  public async getBannerAlerts(): Promise<ContextualRequestedBannerAlert[]> {
    await this.getInternalBanners();
    return this.bannerAlerts;
  }
}
